import { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext.jsx";
import { grantsApi, evaluationsApi } from "../lib/api.js";
import { SCORING_CRITERIA, computeWeightedAverage, scoreBand } from "../lib/constants.js";

function initialScores() {
  return SCORING_CRITERIA.map((c) => ({
    criterion: c.label,
    score: "",
    weight: c.defaultWeight,
  }));
}

export default function EvaluationForm() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const grantId = params.get("grant");

  const [grant, setGrant] = useState(null);
  const [scores, setScores] = useState(initialScores);
  const [feedback, setFeedback] = useState("");
  const [status, setStatus] = useState("submitted");
  const [loading, setLoading] = useState(Boolean(grantId));
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [saved, setSaved] = useState(false);

  const loadGrant = useCallback(
    async (id) => {
      setLoading(true);
      setError("");
      try {
        const res = await grantsApi.get(id);
        setGrant(res.data.grant);
        const existing = await evaluationsApi
          .list(`?grant=${id}`)
          .then((r) => r.data.evaluations.find((e) => e.evaluator?._id === user?.userId || e.evaluator === user?.userId));
        if (existing && existing.status !== "submitted") {
          setScores(
            existing.scores.map((s) => ({ criterion: s.criterion, score: String(s.score), weight: s.weight }))
          );
          setFeedback(existing.feedback || "");
          setStatus(existing.status);
        } else if (existing) {
          setScores(
            existing.scores.map((s) => ({ criterion: s.criterion, score: String(s.score), weight: s.weight }))
          );
          setFeedback(existing.feedback || "");
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    },
    [user?.userId]
  );

  useEffect(() => {
    if (grantId) loadGrant(grantId);
    else setLoading(false);
  }, [grantId, loadGrant]);

  const finalScore = useMemo(() => {
    const numeric = scores.map((s) => ({ criterion: s.criterion, score: Number(s.score) || 0, weight: Number(s.weight) || 0 }));
    return computeWeightedAverage(numeric);
  }, [scores]);

  const band = scoreBand(finalScore);

  const setScore = (index, field, value) => {
    setScores((prev) => prev.map((s, i) => (i === index ? { ...s, [field]: value } : s)));
  };

  const allScored = scores.every((s) => s.score !== "" && Number(s.score) >= 0 && Number(s.score) <= 100);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSaved(false);
    if (!grant) {
      setError("Select a grant to evaluate first.");
      return;
    }
    if (!allScored) {
      setError("All criteria scores must be between 0 and 100.");
      return;
    }
    setSubmitting(true);
    try {
      await evaluationsApi.create({
        grantId: grant._id,
        scores: scores.map((s) => ({ criterion: s.criterion, score: Number(s.score), weight: Number(s.weight) })),
        feedback,
        status,
      });
      setSaved(true);
      setTimeout(() => navigate("/"), 900);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="card p-10 text-center text-sm text-ink-500">Loading assessment…</div>;

  if (!grant) {
    return <GrantPicker onPick={(id) => navigate(`/evaluate?grant=${id}`)} />;
  }

  return (
    <div className="space-y-6">
      <button onClick={() => navigate("/")} className="text-sm text-brand-700 hover:underline">← Back to Dashboard</button>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-ink-900">Active Assessment</h1>
          <p className="text-sm text-ink-500">{grant.projectTitle} · {grant.organization}</p>
        </div>
        <div className="card px-4 py-3 text-right">
          <p className="text-xs uppercase tracking-wide text-ink-400">Real-time Final Score</p>
          <p className={`text-3xl font-bold ${band.classes}`}>{finalScore}</p>
          <p className="text-xs text-ink-400">{band.label}</p>
        </div>
      </div>

      {error && <div role="alert" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
      {saved && <div role="status" className="rounded-md bg-green-50 px-3 py-2 text-sm text-green-700">Evaluation submitted successfully.</div>}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <section className="card overflow-hidden">
          <div className="border-b border-ink-200 px-4 py-3 text-sm font-semibold text-ink-800">External Document</div>
          <div className="h-[70vh] bg-ink-100">
            <iframe
              title={`External document for ${grant.projectTitle}`}
              src={grant.documentUrl}
              className="h-full w-full"
              sandbox="allow-scripts allow-same-origin"
            />
          </div>
          <div className="px-4 py-2 text-xs text-ink-400">
            Source: <a href={grant.documentUrl} target="_blank" rel="noopener noreferrer" className="text-brand-700 hover:underline break-all">{grant.documentUrl}</a>
          </div>
        </section>

        <section>
          <form onSubmit={handleSubmit} className="card space-y-4 p-5" noValidate>
            <h2 className="text-lg font-semibold text-ink-900">Multi-Criteria Scoring</h2>
            {SCORING_CRITERIA.map((c, i) => (
              <div key={c.key} className="rounded-lg border border-ink-200 p-3">
                <div className="flex items-center justify-between">
                  <label htmlFor={`score-${c.key}`} className="text-sm font-medium text-ink-800">{c.label}</label>
                  <span className="text-xs text-ink-400">Weight: {scores[i].weight}</span>
                </div>
                <p className="mb-2 text-xs text-ink-500">{c.description}</p>
                <div className="flex items-center gap-3">
                  <input
                    id={`score-${c.key}`}
                    type="number"
                    min="0"
                    max="100"
                    step="1"
                    className="input max-w-[120px]"
                    placeholder="0–100"
                    value={scores[i].score}
                    onChange={(e) => setScore(i, "score", e.target.value)}
                  />
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={scores[i].score === "" ? 0 : Number(scores[i].score)}
                    onChange={(e) => setScore(i, "score", e.target.value)}
                    className="flex-1 accent-brand-600"
                    aria-label={`${c.label} slider`}
                  />
                  <span className="w-10 text-right text-sm font-semibold text-ink-800">
                    {scores[i].score === "" ? "—" : scores[i].score}
                  </span>
                </div>
              </div>
            ))}

            <div>
              <label htmlFor="feedback" className="label">Justification &amp; Feedback</label>
              <textarea
                id="feedback"
                rows={4}
                className="input"
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder="Provide audit justification for the scored ratings…"
              />
            </div>

            <div className="flex items-center gap-3">
              <label htmlFor="status" className="label mb-0">Status</label>
              <select id="status" className="input max-w-[160px]" value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="draft">Draft (save)</option>
                <option value="submitted">Submitted (final)</option>
              </select>
            </div>

            <div className="flex items-center justify-between border-t border-ink-200 pt-4">
              <div>
                <p className="text-xs uppercase tracking-wide text-ink-400">Weighted Average</p>
                <p className={`text-2xl font-bold ${band.classes}`}>{finalScore}</p>
              </div>
              <button type="submit" className="btn-primary" disabled={submitting}>
                {submitting ? "Submitting…" : status === "draft" ? "Save Draft" : "Submit Evaluation"}
              </button>
            </div>
          </form>
        </section>
      </div>
    </div>
  );
}

function GrantPicker({ onPick }) {
  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const res = await grantsApi.list("?limit=100");
        setGrants(res.data.grants);
      } finally {
        setLoading(false);
      }
    })();
  }, []);
  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold text-ink-900">Select a Grant to Evaluate</h1>
      {loading ? (
        <div className="card p-10 text-center text-sm text-ink-500">Loading grants…</div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {grants.map((g) => (
            <button key={g.id} onClick={() => onPick(g.id)} className="card p-4 text-left hover:border-brand-400">
              <p className="font-medium text-ink-900">{g.projectTitle}</p>
              <p className="text-sm text-ink-500">{g.organization}</p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
